import { useState } from 'react';
import { useActiveBusiness } from '../../app/ActiveBusinessLayout.js';
import type { BusinessDoc } from '../../shared/api/converters.js';
import {
  Badge,
  Banner,
  Button,
  Card,
  ChatBubble,
  EmptyState,
  Field,
  Icon,
  Input,
  PageHeader,
  StatusDot,
  Transcript,
} from '@acs/ui';
import { useI18n } from '../../i18n/index.js';
import type { TranslationKey } from '../../i18n/index.js';
import { useBrowserCall, type CallStatus } from './useBrowserCall.js';

const statusKey = (s: CallStatus) => `testCall.status.${s}` as TranslationKey;

/**
 * Settings › Test call (doc 13a §6): talk to the AI receptionist from the
 * browser to emulate a phone call, with the live transcript and every tool
 * call shown. Anything it books is real. Setup's throwaway version is
 * {@link TryCallStage}.
 */
export function TestCallPanel({
  business,
  showHeader = false,
}: {
  business: BusinessDoc;
  /** The page renders the section header. */
  showHeader?: boolean;
}) {
  const { t } = useI18n();
  // Simulated caller ID (doc 14 D2): without it the tools behave as on a real
  // withheld-number call.
  const [simulatedFrom, setSimulatedFrom] = useState('');
  const { status, active, error, turns, session, audioRef, start, stop } = useBrowserCall({
    business,
    demo: false,
    simulatedFrom,
  });

  return (
    <>
      {showHeader && (
        <PageHeader
          title={t('testCall.title')}
          subtitle={t('testCall.subtitle', { business: business.name })}
          actions={
            <Badge
              dot
              tone={status === 'live' ? 'success' : status === 'error' ? 'danger' : 'neutral'}
            >
              {t(statusKey(status))}
            </Badge>
          }
        />
      )}

      <div className="test-call">
        <Card>
          <p className="muted" style={{ marginTop: 0 }}>
            {t('testCall.savedWarning')}
            {session && ` ${t('testCall.timeLimit', { minutes: Math.round(session.maxSeconds / 60) })}`}
          </p>
          {session?.experiment && (
            <div style={{ marginBottom: 12 }}>
              <Banner tone="info" title={session.experiment}>{t('testCall.experiment', { model: session.model })}</Banner>
            </div>
          )}

          <Field label={t('testCall.callerId')} hint={t('testCall.callerIdHint')}>
            <Input
              mono
              type="tel"
              inputMode="tel"
              placeholder={t('addClient.phonePlaceholder')}
              value={simulatedFrom}
              onChange={(e) => setSimulatedFrom(e.target.value)}
              disabled={active}
            />
          </Field>

          <div className="test-call-controls">
            {active ? (
              <Button variant="danger" onClick={stop} leadingIcon={<Icon name="x-circle" size={18} />}>
                {t('testCall.end')}
              </Button>
            ) : (
              <Button size="lg" onClick={() => void start()} leadingIcon={<Icon name="mic" size={18} />}>
                {t('testCall.start')}
              </Button>
            )}
            {status === 'live' && (
              <span className="muted" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <StatusDot tone="success" pulse /> {t('testCall.listening')}
              </span>
            )}
          </div>

          {error && (
            <div style={{ marginTop: 14 }}>
              <Banner tone="danger" title={t('testCall.failed')}>{error}</Banner>
            </div>
          )}
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <audio ref={audioRef} autoPlay />
        </Card>

        <Card title={t('testCall.transcript')}>
          {turns.length === 0 ? (
            <EmptyState
              borderless
              icon={<Icon name="mic" size={24} />}
              iconTone="neutral"
              title={t('testCall.nothingSaid')}
              description={t('testCall.nothingSaidDescription')}
            />
          ) : (
            <Transcript>
              {turns.map((turn, i) =>
                turn.role === 'tool' ? (
                  <div key={i} className="turn turn-tool">{turn.text}</div>
                ) : (
                  <ChatBubble
                    key={i}
                    speaker={turn.role === 'you' ? 'caller' : 'assistant'}
                    who={turn.role === 'you' ? t('testCall.you') : t('testCall.ai')}
                  >
                    {turn.text}
                  </ChatBubble>
                ),
              )}
            </Transcript>
          )}
        </Card>
      </div>
    </>
  );
}

/** The Test call section of the app (doc 13a §6) — the panel under a heading. */
export function TestCallPage() {
  const business = useActiveBusiness();
  return <TestCallPanel business={business} showHeader />;
}
